import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Container, Layers, Network, Settings, CheckCircle } from 'lucide-react';

const ProjectKubernetesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 via-red-600 to-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Container className="w-5 h-5" />
              <span className="font-semibold">DevOps & Cloud Project</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Kubernetes Container Orchestration
            </h1>
            <p className="text-xl md:text-2xl text-orange-100 max-w-3xl mx-auto leading-relaxed">
              Deploy and manage containerized applications at scale using Kubernetes
            </p>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-3xl p-8 md:p-12 border border-orange-100">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Master Kubernetes by deploying, scaling, and managing containerized microservices in production. You'll learn container orchestration, service mesh, monitoring, and everything needed to run production workloads on Kubernetes.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-600 mb-2">6-8 Weeks</div>
                <div className="text-gray-600">Duration</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-red-600 mb-2">Advanced</div>
                <div className="text-gray-600">Level</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">Certificate</div>
                <div className="text-gray-600">Upon Completion</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Build */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            What You'll Build
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: Container,
                title: "Microservices Deployment",
                description: "Deploy multi-container applications with Docker and Kubernetes pods"
              },
              {
                icon: Layers,
                title: "Auto-Scaling & Load Balancing",
                description: "Configure horizontal pod autoscaling and service load balancing"
              },
              {
                icon: Network,
                title: "Service Mesh (Istio)",
                description: "Implement service mesh for traffic management, security, and observability"
              },
              {
                icon: Settings,
                title: "Monitoring & Logging",
                description: "Set up Prometheus, Grafana, and ELK stack for complete observability"
              }
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Skills You'll Learn */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Skills You'll Master
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Kubernetes Architecture & Components",
              "Docker Containerization",
              "Helm Charts for Package Management",
              "ConfigMaps, Secrets & Volumes",
              "Ingress Controllers & Load Balancing",
              "Service Mesh (Istio/Linkerd)",
              "Prometheus & Grafana Monitoring",
              "Kubernetes Security Best Practices",
              "Rolling Updates & Rollbacks",
              "Multi-Cluster Management"
            ].map((skill, index) => (
              <div key={index} className="flex items-center gap-3 bg-orange-50 rounded-xl p-4">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-800 font-medium">{skill}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Deliverables */}
      <section className="py-16 bg-gradient-to-br from-orange-600 to-red-600 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            Your Final Portfolio Piece
          </h2>
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/20">
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 text-gray-900 font-bold">1</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Production Kubernetes Cluster</h3>
                  <p className="text-orange-100">Fully operational multi-node K8s cluster with deployed microservices</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 text-gray-900 font-bold">2</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Kubernetes Manifests & Helm Charts</h3>
                  <p className="text-orange-100">Complete YAML configurations and Helm charts for all services</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 text-gray-900 font-bold">3</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Architecture Documentation</h3>
                  <p className="text-orange-100">Detailed architecture diagrams and deployment documentation</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to Master Kubernetes?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join CareerLift and start building portfolio projects that get you hired
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.location.href = '/contact'}
              className="bg-gradient-to-r from-orange-600 to-red-600 text-white font-bold px-10 py-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
            >
              Start Learning
            </button>
            <button
              onClick={() => window.location.href = '/programs'}
              className="bg-gray-100 hover:bg-gray-200 text-gray-900 font-bold px-10 py-4 rounded-xl transition-all"
            >
              View All Projects
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectKubernetesPage;
